import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { MessageSquare, Search, Plus, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import Button from '../components/Button';
import ConversationList from '../components/messaging/ConversationList';
import MessageList from '../components/messaging/MessageList';
import MessageComposer from '../components/messaging/MessageComposer';
import NewMessageModal from '../components/messaging/NewMessageModal';
import GroupChatModal from '../components/messaging/GroupChatModal';
import { useMessaging } from '../hooks/useMessaging';
import UserName from '../components/UserName';
import Avatar from '../components/Avatar';
import supabase from '../lib/supabase';

export default function Messages() {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [showGroupChat, setShowGroupChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => {
    const stored = localStorage.getItem('messagesSidebarExpanded');
    return stored ? JSON.parse(stored) : true;
  });
  const selectedId = searchParams.get('id');
  const [selectedConversation, setSelectedConversation] = useState(null);

  // Conversation polling refs
  const conversationPollRef = useRef(null);
  const lastConversationPollRef = useRef(null);

  // Force sidebar expanded on mobile when no conversation is selected
  useEffect(() => {
    if (!selectedId && window.innerWidth < 768) {
      setIsSidebarExpanded(true);
    }
  }, [selectedId]);

  // Conversation list polling function
  const pollConversations = React.useCallback(async () => {
    if (!user) return;

    try {
      console.log('🔄 [CONV POLL] Checking for conversation updates...');
      
      const pollTimestamp = lastConversationPollRef.current || 
        new Date(Date.now() - 10000).toISOString();
      
      // Check for updated conversations
      const { data: groupIds } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user.id);

      const { data: updatedConversations, error } = await supabase
        .from('conversations')
        .select('id, updated_at')
        .or(
          `user1_id.eq.${user.id},` +
          `user2_id.eq.${user.id},` +
          `id.in.(${groupIds?.map(g => g.conversation_id) || []})`
        )
        .gte('updated_at', pollTimestamp);

      if (error) throw error;

      if (updatedConversations && updatedConversations.length > 0) {
        console.log('📨 [CONV POLL] Found updated conversations:', updatedConversations.length);
        await fetchConversations();
      }
      
      lastConversationPollRef.current = new Date().toISOString();
      
    } catch (err) {
      console.error('❌ [CONV POLL] Error:', err);
    }
  }, [user]);

  // Fetch conversations function
  const fetchConversations = React.useCallback(async () => {
    if (!user) return;

    try {
      console.log('🔄 [CONV] Fetching conversations...');
      setIsLoading(true);
      setError(null);

      const { data: groupIds } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user.id);

      const { data, error: fetchError } = await supabase
        .from('conversations')
        .select(`
          *,
          user1:user1_id(id, full_name, display_name, verified, avatar_url),
          user2:user2_id(id, full_name, display_name, verified, avatar_url),
          participants:conversation_participants(
            user_id,
            role,
            is_muted,
            last_read_at,
            user:user_profiles(
              id, full_name, display_name, verified, avatar_url
            )
          )
        `)
        .or(
          `user1_id.eq.${user.id},` +
          `user2_id.eq.${user.id},` +
          `id.in.(${groupIds?.map(g => g.conversation_id) || []})`
        )
        .order('last_message_at', { ascending: false });

      if (fetchError) throw fetchError;
      setConversations(data || []);
      console.log('✅ [CONV] Conversations fetched:', data?.length || 0);

      if (selectedId) {
        const selected = data?.find(conv => conv.id === selectedId);
        setSelectedConversation(selected);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user, selectedId, pollConversations]);

  // Use messaging hook with proper callback
  const {
    messages = [],
    isLoading: isLoadingMessages = false,
    error: messageError = null,
    hasMore = false,
    sendMessage,
    editMessage,
    loadMoreMessages
  } = useMessaging(selectedId || undefined, fetchConversations) || {};

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('messagesSidebarExpanded', JSON.stringify(isSidebarExpanded));
  }, [isSidebarExpanded]);

  // Auto-collapse sidebar on mobile when a conversation is selected
  useEffect(() => {
    if (selectedId && window.innerWidth < 768) {
      setIsSidebarExpanded(false);
    }
  }, [selectedId]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Conversation list real-time + polling setup
  useEffect(() => {
    if (!user) return;

    console.log('🚀 [CONV] Starting conversation polling...');

    // Just use polling - it's more reliable than real-time
    lastConversationPollRef.current = new Date().toISOString();
    conversationPollRef.current = setInterval(pollConversations, 2000); // Poll every 2 seconds

    return () => {
      console.log('🧹 [CONV] Cleaning up conversation polling');
      if (conversationPollRef.current) {
        clearInterval(conversationPollRef.current);
        conversationPollRef.current = null;
      }
    };
  }, [user, pollConversations]);

  const handleSendMessage = async (content, attachments) => {
    if (user && (content.trim() || (attachments && attachments.length > 0))) {
      if (sendMessage) {
        await sendMessage(content, attachments);
      }
    }
  };

  const handleEditMessage = async (messageId, content) => {
    if (!user || !editMessage) return;
    await editMessage(messageId, content);
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
        <MessageSquare className="w-12 h-12 text-neutral mb-4" />
        <h2 className="text-xl font-bold font-montserrat text-primary mb-2">Sign in to Message</h2>
        <p className="text-neutral text-sm mb-4">Connect with other music professionals</p>
        <Button variant="primary" as="a" href="/auth">
          Sign In
        </Button>
      </div>
    );
  }

  const getOtherUser = (conversation) => {
    if (!conversation) return null;
    if (conversation.is_group) return null;
    return conversation.user1_id === user.id ? conversation.user2 : conversation.user1;
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex bg-secondary-light relative overflow-hidden">
      {/* Sidebar */}
      <div 
        className={`fixed md:relative left-0 top-0 h-full bg-secondary-light border-r border-neutral border-opacity-10 transition-all duration-300 ease-in-out z-20 ${
          isSidebarExpanded ? 'w-full md:w-80' : selectedId ? 'w-0 md:w-16' : 'w-full md:w-80'
        }`}
      >
        <div className={`h-full flex flex-col overflow-hidden
    ${isSidebarExpanded ? 'pb-16' : ''}
    ${!isSidebarExpanded && selectedId ? 'md:items-center' : ''}`}>
          {/* Header */}
          <div className={`p-4 border-b border-neutral border-opacity-10 ${!isSidebarExpanded && selectedId && 'md:border-none'}`}>
            {(isSidebarExpanded || !selectedId) ? (
              <>
                <div className="relative mb-6">
                  <h1 className="text-3xl font-bold font-dillan text-primary text-center">Messages</h1>
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-light" />
                  <input
                    type="text"
                    placeholder="Search conversations"
                    className="w-56 h-10 pl-10 pr-4 rounded-full border border-neutral bg-neutral text-surface-light placeholder:text-surface-light focus:border-neutral focus:ring-0 focus:outline-none"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowGroupChat(true)}
                    >
                      <Users className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowNewMessage(true)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="hidden md:flex flex-col items-center gap-2">
                <button
                  onClick={() => setIsSidebarExpanded(true)}
                  className="p-2 rounded-full bg-surface-light hover:bg-surface transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-neutral" />
                </button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowGroupChat(true)}
                  title="New Group Chat"
                >
                  <Users className="w-4 h-4" />
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowNewMessage(true)}
                  title="New Message"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Conversations List */}
          {(isSidebarExpanded || !selectedId) && (
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
                </div>
              ) : error ? (
                <div className="p-4 text-center text-secondary-dark">
                  {error}
                </div>
              ) : (
                <ConversationList
                  conversations={conversations}
                  selectedId={selectedId || undefined}
                  onSelect={(id) => {
                    setSearchParams({ id });
                    if (window.innerWidth < 768) {
                      setIsSidebarExpanded(false);
                    }
                  }}
                  searchQuery={searchQuery}
                  onConversationRemoved={fetchConversations}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div 
        className={`flex-1 flex flex-col transition-all duration-300 ease-in-out ${
          isSidebarExpanded && selectedId ? 'hidden md:flex md:ml-0' : 
          !isSidebarExpanded && selectedId ? 'flex ml-0 md:ml-16' : 
          'hidden md:flex md:ml-0'
        }`}
      >
        {selectedId ? (
          <>
            {/* Conversation Header */}
            <div className="flex items-center gap-2 p-4 border-b border-primary">
              <button
                onClick={() => setIsSidebarExpanded(true)}
                className="md:hidden"
              >
                <ChevronLeft className="w-5 h-5 text-primary" />
              </button>
              {selectedConversation && (
                <div className="flex items-center gap-3">
                  {selectedConversation.is_group ? (
                    <>
                      <Avatar
                        src={selectedConversation.avatar_url}
                        alt={selectedConversation.name || 'Group Chat'}
                        size="md"
                      />
                      <h2 className="text-lg font-semibold text-primary">
                        {selectedConversation.name || 'Group Chat'}
                      </h2>
                    </>
                  ) : (
                    <>
                      <Avatar
                        src={getOtherUser(selectedConversation)?.avatar_url}
                        alt={getOtherUser(selectedConversation)?.display_name || getOtherUser(selectedConversation)?.full_name}
                        size="md"
                      />
                      <UserName
                        name={getOtherUser(selectedConversation)?.display_name || getOtherUser(selectedConversation)?.full_name}
                        verified={getOtherUser(selectedConversation)?.verified}
                        userId={getOtherUser(selectedConversation)?.id}
                        size="lg"
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Message List */}
            {isLoadingMessages ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
              </div>
            ) : messageError ? (
              <div className="flex-1 flex items-center justify-center text-secondary-dark">
                {messageError}
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <MessageList
                  messages={messages}
                  currentUserId={user.id}
                  onEditMessage={handleEditMessage}
                />
              </div>
            )}

            {/* Message Composer */}
            <MessageComposer
              onSend={handleSendMessage}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 text-neutral mx-auto mb-4" />
              <h2 className="text-xl font-bold font-montserrat text-primary mb-2">
                Select a Conversation
              </h2>
              <p className="text-neutral mb-4">
                Choose a conversation from the list or start a new one
              </p>
              <div className="flex gap-2 justify-center">
                <Button
                  variant="primary"
                  onClick={() => setShowGroupChat(true)}
                >
                  <Users className="w-4 h-4 mr-1.5" />
                  New Group
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setShowNewMessage(true)}
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  New Message
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showNewMessage && (
        <NewMessageModal
          onClose={() => setShowNewMessage(false)}
          onSuccess={(conversationId) => {
            fetchConversations().then(() => {
              setSearchParams({ id: conversationId });
              setShowNewMessage(false);
            });
          }}
        />
      )}

      {showGroupChat && (
        <GroupChatModal
          onClose={() => setShowGroupChat(false)}
          onSuccess={(conversationId) => {
            fetchConversations().then(() => {
              setSearchParams({ id: conversationId });
              setShowGroupChat(false);
            });
          }}
        />
      )}
    </div>
  );
}